'use client';

/**
 * Hook for managing thread storage (Cognito for authenticated users, cookies for unauthenticated)
 * 
 * Provides functions to store/retrieve threads from either Cognito attributes or cookies
 */

import { useState, useCallback } from 'react';
import type { SupportThread, ThreadId, CookieStorageConfig } from '../types/support-chat';
import {
  getUserSupportThreads,
  addUserSupportThread,
  removeUserSupportThread,
  findUserSupportThread,
  updateUserSupportThreads,
} from '../lib/cognito-client';
import {
  getCookieSupportThreads,
  addCookieSupportThread,
  removeCookieSupportThread,
  findCookieSupportThread,
  updateCookieSupportThreads,
} from '../lib/cookie-storage';

interface UseThreadStorageOptions {
  /** Cognito access token (null for unauthenticated users) */
  accessToken: string | null;
  /** Custom attribute name for Cognito (default: 'support_threads') */
  attributeName?: string;
  /** Cookie storage configuration for unauthenticated users */
  cookieStorage?: CookieStorageConfig;
}

export function useThreadStorage(options: UseThreadStorageOptions) {
  const { accessToken, attributeName = 'support_threads', cookieStorage } = options;
  const [threads, setThreads] = useState<SupportThread[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Determine if we should use Cognito or cookies
  const useCognito = accessToken !== null;
  const useCookies = !useCognito && cookieStorage !== undefined;

  /**
   * Load threads from Cognito or cookies
   */
  const loadThreads = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      let loadedThreads: SupportThread[] = [];

      if (useCognito && accessToken) {
        loadedThreads = await getUserSupportThreads(accessToken, attributeName);
      } else if (useCookies && cookieStorage) {
        loadedThreads = getCookieSupportThreads(cookieStorage.cookieName);
      } else {
        setError('No storage method available. Provide accessToken or cookieStorage config.');
        return;
      }

      setThreads(loadedThreads);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load threads';
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [accessToken, attributeName, cookieStorage, useCognito, useCookies]);

  /**
   * Add a new thread
   */
  const addThread = useCallback(
    async (thread: SupportThread): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        let success = false;

        if (useCognito && accessToken) {
          success = await addUserSupportThread(accessToken, thread, attributeName);
        } else if (useCookies && cookieStorage) {
          success = addCookieSupportThread(cookieStorage.cookieName, thread, cookieStorage);
        } else {
          setError('No storage method available. Provide accessToken or cookieStorage config.');
          return false;
        }

        if (success) {
          setThreads((prev) => {
            const exists = prev.some((t) => t.threadId === thread.threadId);
            if (exists) {
              return prev.map((t) => (t.threadId === thread.threadId ? thread : t));
            }
            return [...prev, thread];
          });
        }
        return success;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to add thread';
        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, attributeName, cookieStorage, useCognito, useCookies]
  );

  /**
   * Remove a thread
   */
  const removeThread = useCallback(
    async (threadId: ThreadId | string): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        let success = false;

        if (useCognito && accessToken) {
          success = await removeUserSupportThread(accessToken, threadId, attributeName);
        } else if (useCookies && cookieStorage) {
          success = removeCookieSupportThread(cookieStorage.cookieName, threadId, cookieStorage);
        } else {
          setError('No storage method available. Provide accessToken or cookieStorage config.');
          return false;
        }

        if (success) {
          setThreads((prev) => prev.filter((t) => t.threadId !== threadId));
        }
        return success;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to remove thread';
        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, attributeName, cookieStorage, useCognito, useCookies]
  );

  /**
   * Find a thread by ID
   */
  const findThread = useCallback(
    async (threadId: ThreadId | string): Promise<SupportThread | null> => {
      // First check local state
      const localThread = threads.find((t) => t.threadId === threadId);
      if (localThread) {
        return localThread;
      }

      // If not found locally, fetch from storage
      try {
        if (useCognito && accessToken) {
          return await findUserSupportThread(accessToken, threadId, attributeName);
        } else if (useCookies && cookieStorage) {
          return findCookieSupportThread(cookieStorage.cookieName, threadId);
        }
        return null;
      } catch {
        return null;
      }
    },
    [accessToken, attributeName, cookieStorage, threads, useCognito, useCookies]
  );

  /**
   * Update all threads
   */
  const updateThreads = useCallback(
    async (updatedThreads: SupportThread[]): Promise<boolean> => {
      setIsLoading(true);
      setError(null);

      try {
        let success = false;

        if (useCognito && accessToken) {
          success = await updateUserSupportThreads(accessToken, updatedThreads, attributeName);
        } else if (useCookies && cookieStorage) {
          success = updateCookieSupportThreads(cookieStorage.cookieName, updatedThreads, cookieStorage);
        } else {
          setError('No storage method available. Provide accessToken or cookieStorage config.');
          return false;
        }

        if (success) {
          setThreads(updatedThreads);
        }
        return success;
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to update threads';
        setError(errorMessage);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [accessToken, attributeName, cookieStorage, useCognito, useCookies]
  );

  return {
    threads,
    isLoading,
    error,
    loadThreads,
    addThread,
    removeThread,
    findThread,
    updateThreads,
  };
}

